import {
    useLayoutEffect,
    useEffect,
    useState
} from "react";
import {
    TouchableOpacity,
    ActivityIndicator,
    ScrollView,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    MarkdownViewer,
    PageContainer,
    Text
} from "ncore-ui-kit";
import {
    useDebouncedDimensions
} from "../../../utils";
import {
    useNavigation,
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    PrivacyPolicyPayloadType
} from "../../../services/restAPI/actions/getPrivacyPolicy/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronLeft,
    UserIcon
} from "lucide-react-native";
import packageJson from "../../../../package.json";
import {
    FooterVersionInfo
} from "../../../components";
import {
    RESTService
} from "../../../services/restAPI";
import {
    storage
} from "../../../storage";

const PrivacyPolicy = () => {
    const {
        windowWidth
    } = useDebouncedDimensions();

    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [data,
        setData] = useState<PrivacyPolicyPayloadType>();
    const [loading,
        setLoading] = useState(true);

    const inset = useSafeAreaInsets();

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        backgroundColor: colors.content.container.default,
                        paddingHorizontal: spaces.spacingMd,
                        paddingTop: inset.top
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    pointerEvents="none"
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("privacy-policy")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    useEffect(() => {
        getPrivacyPolicy();
    }, []);

    const getPrivacyPolicy = () => {
        RESTService.action("GetPrivacyPolicy", {
            language: storage.getString("language") ?? "tr-TR"
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setData(res.payload);
                }
                setLoading(false);
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const renderInfo = () => {
        return <View
            style={[
                stylesheet.contentContainer,
                {
                    marginBottom: spaces.spacingMd,
                    marginTop: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.infoCardContainer,
                    {
                        backgroundColor: colors.content.container.default,
                        width: windowWidth - spaces.spacingLg * 2,
                        borderRadius: radiuses.sm,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.iconContent,
                        {
                            backgroundColor: colors.content.container.success,
                            borderRadius: radiuses.xs
                        }
                    ]}
                >
                    <UserIcon
                        color={colors.content.icon.success}
                        size={30}
                    />
                </View>
                <View
                    style={[
                        stylesheet.titleContainer,
                        {
                            paddingHorizontal: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                    >
                        {localize("privacy-policy")}
                    </Text>
                    <View
                        style={stylesheet.dateContainer}
                    >
                        <Text
                            variant="labelLargeSize"
                            color="low"
                        >
                            {`Sürüm ${packageJson.version}`}
                        </Text>
                        <View
                            style={[
                                stylesheet.dot,
                                {
                                    backgroundColor: colors.content.text.low,
                                    marginHorizontal: spaces.spacingSm,
                                    borderRadius: radiuses.form
                                }
                            ]}
                        />
                        <Text
                            variant="labelLargeSize"
                            color="low"
                        >
                            {"1 Ocak 2026"}
                        </Text>
                    </View>
                </View>
            </View>
            <MarkdownViewer
                content={data?.content || ""}
                style={{
                    marginTop: spaces.spacingMd
                }}
            />
        </View>;
    };

    if(loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={false}
        style={stylesheet.container}
    >
        <ScrollView
            style={stylesheet.flex1}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
        >
            {renderInfo()}
        </ScrollView>
        <FooterVersionInfo
            isProfile={false}
        />
    </PageContainer>;
};
export default PrivacyPolicy;
